import Router from 'next/router';
import { useCallback } from 'react';

import { type Board } from '@air/api/types';

import { createBoardRoute } from '~/constants/routes';
import { useGoToPrivateBoardPage } from '~/hooks/useGoToPrivateBoardPage';
import { type LocationBoard } from '~/store/router/types';
import { getBoardIdFromPath, getClipIdFromPath } from '~/utils/PathUtils';

/**
 * Opens the board a board pill cites. The route needs the title, so every caller has to wait on the
 * board itself rather than navigating from the pill's `boardId` alone.
 */
export const useOpenWorkspaceAgentBoardPill = () => {
  const { goToBoardPage } = useGoToPrivateBoardPage();

  const openBoardPill = useCallback(
    (board: Board | LocationBoard) => {
      // Not `Router.query.boardId`: the `/b/[boardId]` segment is `slug-uuid`, so it never equals an id.
      const currentBoardId = getBoardIdFromPath(Router.asPath);

      if (currentBoardId === board.id) {
        // `goToBoardPage` compares against that same `slug-uuid` param, so it does not recognise this as the
        // board it is already on. With a clip in the URL, pushing the plain board route is what closes the
        // asset modal over the board; with the board page itself open, pushing would only drop its
        // search / filter params. The panel stands down whenever a canvas is open
        // (`useWorkspaceAgentPanelCanvasOpen`), so the asset modal is the only overlay a history chip can be
        // opened over and a canvas check here would be unreachable.
        if (getClipIdFromPath(Router.asPath)) {
          // Voided here and below: the router and `goToBoardPage` report their own rejections, which are not actionable here.
          void Router.push(createBoardRoute(board.id, board.title));
        }

        return;
      }

      void goToBoardPage({ board });
    },
    [goToBoardPage],
  );

  return { openBoardPill };
};
